import { SkeletonBox } from '@billy/design-system'
import { ComponentType, lazy, Suspense, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { createCashPilotNavigate } from '@features/cashPilot/createCashPilotNavigate'
import type { CashPilotNavigatePath } from '@features/cashPilot/types/cashPilotNavigatePath'
import { useIsCashPilotEnabled } from '@features/cashPilot/useIsCashPilotEnabled'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { Scope } from '../../../../enums/scope'
import { useIsBasicPlan } from '../../../../hooks/useIsBasicPlan'
import { useSegment } from '../../../../hooks/useSegment'
import { getAccessToken } from '../../../../utils'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { Widget } from '../../elements/Widget'

type CashPilotWidgetVariant = 'connectBank' | 'nominal' | 'upgrade'

interface CashPilotWidgetHostProps {
  accessToken: string
  apiUrl: string
  language: string
  navigate: (path: CashPilotNavigatePath) => void
  organizationId: string
  theme: 'light'
  trackEvent: (event: string, properties?: Record<string, unknown>) => void
  variant: CashPilotWidgetVariant
}

const LazyCashPilotWidget = lazy(() =>
  import('@shinetools/cashpilot').then((module) => ({
    default: module.CashPilotWidget as unknown as ComponentType<CashPilotWidgetHostProps>,
  })),
)

export const CashPilot = () => {
  const { i18n } = useTranslation()
  const { organization } = useUserOrganization()
  const accessToken = getAccessToken()
  const navigate = useNavigate()
  const { track } = useSegment()
  const { isLoading } = useIsBasicPlan()
  const hasConnectedBankAccounts = useIsCashPilotEnabled()

  const handleNavigate = useMemo(
    () => createCashPilotNavigate(organization?.url, navigate),
    [navigate, organization?.url],
  )

  // As specified in `isAuthorized`'s documentation, not memoizing its use
  const getVariant = () => {
    const canRead = isAuthorized(Scope.CashFlowAnalyticsRead)
    const canUpsell = isAuthorized(Scope.CashFlowAnalyticsUpsell)

    if (!canRead && canUpsell) {
      return 'upgrade'
    }

    if (!hasConnectedBankAccounts) {
      return 'connectBank'
    }

    return 'nominal'
  }

  if (!organization || !accessToken) {
    return null
  }

  return (
    <Widget autoHeight isLoading={isLoading}>
      <Suspense fallback={<SkeletonBox fullWidth height={150} />}>
        <LazyCashPilotWidget
          accessToken={accessToken}
          apiUrl={window.ENV.newApiUrl}
          language={i18n.language}
          navigate={handleNavigate}
          organizationId={organization.id}
          theme="light"
          trackEvent={track}
          variant={getVariant()}
        />
      </Suspense>
    </Widget>
  )
}
